/**
 * Orçamento por finalidade.
 *
 * As preferências de um contacto são uma linha por combinação (zona × tipo ×
 * finalidade). O orçamento das linhas de ARRENDAMENTO é a renda mensal; o das
 * restantes é o orçamento de compra. Misturá-los dava "800 € – 300 000 €".
 */

/** Tipos de negócio que são arrendamento — a mesma lista do servidor. */
export const RENT_BUSINESS_TYPES = [
  "To Lease",
  "For Vacations",
  "Local Accommodation",
];

export interface BudgetRange {
  min: number | null;
  max: number | null;
}

export interface PurposeBudgets {
  /** Orçamento de compra (linhas que não são de arrendamento). */
  budget: BudgetRange | null;
  /** Renda mensal pretendida (linhas de arrendamento). */
  rentBudget: BudgetRange | null;
  hasRentPurpose: boolean;
  hasOtherPurpose: boolean;
}

function toAmount(value: any): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(`${value}`.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function rangeOf(rows: any[]): BudgetRange | null {
  const mins = rows
    .map((row) => toAmount(row?.budgetMin))
    .filter((value): value is number => value !== null);
  const maxs = rows
    .map((row) => toAmount(row?.budgetMax))
    .filter((value): value is number => value !== null);

  const min = mins.length ? Math.min(...mins) : null;
  const max = maxs.length ? Math.max(...maxs) : null;
  if (min === null && max === null) return null;
  if (min !== null && max !== null && min > max) return { min: max, max: min };
  return { min, max };
}

/**
 * Separa os orçamentos das linhas de preferência.
 *
 * `isRentRow` diz se uma linha é de arrendamento. Por omissão lê a descrição
 * do tipo de negócio que vem na linha; quem só tem ids passa o seu critério.
 */
export function splitPurposeBudgets(
  rows: any[] | null | undefined,
  isRentRow: (row: any) => boolean = isRentPreferenceRow,
): PurposeBudgets {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const withPurpose = list.filter(
    (row) => row?.businessTypeId || row?.businessType,
  );
  const rentRows = withPurpose.filter((row) => isRentRow(row));
  const otherRows = list.filter((row) => !rentRows.includes(row));

  return {
    budget: rangeOf(otherRows),
    rentBudget: rangeOf(rentRows),
    hasRentPurpose: rentRows.length > 0,
    hasOtherPurpose: withPurpose.length > rentRows.length,
  };
}

export function isRentPreferenceRow(row: any): boolean {
  const description = `${row?.businessType?.description ?? ""}`.trim();
  return RENT_BUSINESS_TYPES.includes(description);
}
